import { z } from 'zod';
import { SchemaTimestamp } from '@axiom/types';

import { AccountSchema } from './account';
import { BaseUserSchema } from './base-user';
import { CandidateSchema } from './candidate';
import { OpportunitySchema } from './opportunity';
import { PositionSchema } from './position';
import { ZodRejectionLossCodeValues } from './candidate-opportunities';
import { ZodCandidateOpportunityCandidateStatuses } from './candidate-opportunities-candidate-statuses';
import {
  ZodCandidateEngagementStates,
  ZodInterviewingStates,
} from './candidate-engagement';
import { CalendarEventSchema } from './calendar';

export const CandidateEngagementDetailsSchema = z.object({
  engagementState: ZodCandidateEngagementStates.nullable(),
  candidateIsInterested: z.boolean().default(false),
  candidateInterestedAt: SchemaTimestamp.nullable(),
  candidateStatus: ZodCandidateOpportunityCandidateStatuses,
  candidateStatusMessage: z.string().nullable(),
  hasMovedPastWarmedWithoutAnsweringWarmingQuestions: z.boolean(),
  previousCandidateStatus: z.string().nullable(),
  rejectionLossCode: ZodRejectionLossCodeValues.nullable(),
  candidate: CandidateSchema.pick({
    id: true,
    profileStatus: true,
  }).extend({
    ownerUser: BaseUserSchema.pick({
      id: true,
      email: true,
      fullName: true,
    }).nullable(),
    preferredEmail: z.string().nullable(),
  }),
  onboardingEmailSentAt: SchemaTimestamp.nullable(),
  opportunity: OpportunitySchema.pick({
    id: true,
    jobName: true,
    accountName: true,
    clientName: true,
    primaryPracticeArea: true,
    primaryFocusArea: true,
    locationCityStateCountry: true,
    selfService: true,
    stageCode: true,
  }).extend({
    account: AccountSchema.pick({
      id: true,
      website: true,
    })
      .extend({
        cxPartner: BaseUserSchema.pick({
          id: true,
          email: true,
          fullName: true,
        }).nullable(),
        ownerUser: BaseUserSchema.pick({
          id: true,
          email: true,
          fullName: true,
        }).nullable(),
      })
      .nullable(),
    deliveryManager: BaseUserSchema.pick({
      id: true,
      email: true,
      fullName: true,
    }).nullable(),
    ownerUser: BaseUserSchema.pick({
      id: true,
      email: true,
      fullName: true,
    }).nullable(),
    salesLead: BaseUserSchema.pick({
      id: true,
      email: true,
      fullName: true,
    }).nullable(),
  }),
  position: PositionSchema.pick({
    id: true,
    startDate: true,
    endDate: true,
    endDateStatus: true,
    estimatedEngagementTotalMonths: true,
    billingHoursPerWeek: true,
    worksite: true,
    description: true,
    businessTitle: true,
  }).nullable(),
  interviews: z.array(
    CalendarEventSchema.pick({
      startTime: true,
      endTime: true,
    }).extend({
      attendees: z.array(z.string()),
      zoomLink: z.string().nullable(),
      interviewingState: ZodInterviewingStates,
    })
  ),
});

export type CandidateEngagementDetails = z.infer<
  typeof CandidateEngagementDetailsSchema
>;
